import { GridViewController } from '../controller/GridViewController';
import { BaseWebComponentElement } from '../../../framework/BaseWebComponentElement';
import { Filter } from '../../gridFilters/types';
import { ColumnDetails } from '../../types/common';
import { GridBaseDataProvider } from '../../GridBaseDataProvider';
import {
  CustomStrings,
  GridAttribute,
  GridColumnRenderers,
  GridHeaderRenderers,
  SELECTION_STORE_CONFIGURATION,
  SelectionStoreConfig,
  ViewConfiguration,
} from '../types';
import { GridColumnConfiguration } from '@nirvana/shared/dtos/types/shared/grid';
import { SelectOption } from '../../../ui/gzSelect/types';
import { GridTitleConfig } from '../../uiElements/gridViewTitle/types';
import { GridFilterUiConfiguration } from '../../../types/filters/types';

export class GridViewWebComponent<
  Column extends number,
  ColumnKeys extends string,
  GridItemType = any
> extends BaseWebComponentElement {
  public controller: GridViewController<Column, ColumnKeys, GridItemType> | null;

  private onDataLoadingStartedFn?: () => Promise<void>;
  private onDataLoadingFinishedWithSuccessFn?: () => Promise<void>;
  private onDataLoadingFinishedWithErrorFn?: () => Promise<void>;
  private columnRenderersObj?: GridColumnRenderers = {};
  private headerRenderersObj?: GridHeaderRenderers = {};
  private rowDetailsRendererFn?: Function;
  private dataProviderRef: GridBaseDataProvider<Column, ColumnKeys>;
  private inlineRowActionsRef?: Array<SelectOption>;
  private pageSizeOptionsRef?: Array<SelectOption>;
  private selectedItemsRef?: Array<GridItemType>;
  private sideMenuParentRef: HTMLElement;
  private _filterDependencyMapping?: ColumnDetails<Column, ColumnKeys>;
  private _columnConfigs: Array<GridColumnConfiguration> = [];
  private _defaultFlexGrowColumn: string;
  private _shouldLoadAsync: boolean;
  private _sortColumnName?: string;
  private _sortDirection?: string;
  public _customStrings: CustomStrings = {};
  private _smartViewsEnabled: boolean;
  private _showFilters = true;
  private _activeViewProps: ViewConfiguration<Column, ColumnKeys>;
  private _activeViewInitialProps: ViewConfiguration<Column, ColumnKeys>;
  private _smartViewDiscardButtonDisabledState: boolean;
  private _smartViewSaveButtonDisabledState: boolean;
  private _showConfigurableArea: boolean;
  private _showGlobalActions: boolean;
  private _titleConfig: GridTitleConfig;
  private _cellClassNameGenerator: Function;
  private _selectionStoreConfig = SELECTION_STORE_CONFIGURATION;
  private _filtersUiConfigs: Array<GridFilterUiConfiguration> = [];
  private _favoriteIconConfig = {};
  public gridLoaded = false;

  private observablePropertyHandler = {
    [GridAttribute.TITLE]: this.setGridTitle.bind(this),
    [GridAttribute.PROPERTIES_SET]: this.renderGrid.bind(this),
  };

  static override get observedAttributes(): string[] {
    return [...BaseWebComponentElement.observedAttributes, GridAttribute.TITLE];
  }

  constructor() {
    super();
    this.initialSetup({
      controllerConstructor: GridViewController,
    });
  }

  private setGridTitle(oldTitleConfig: string, newTitleConfig: string): void {
    if (this.controller) {
      const parsedTitleConfig = JSON.parse(newTitleConfig);

      (this.controller as GridViewController<Column, ColumnKeys>).setTitleProps(
        parsedTitleConfig,
        this.noEscapeSpaceInTitle
      );
    }
  }

  private renderGrid(): void {
    const shouldRender = this.propertiesSet && this.controller;

    if (shouldRender) {
      this.controller!.render();
    }
  }

  public setRowData(index: number, data) {
    if (this.controller) {
      this.controller.view.dom.$grid.items[index] = data;
    }
  }

  public resetColumns() {
    if (this.controller?.view) {
      this.controller.view.resetColumns();
    }
  }

  override attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    this.observablePropertyHandler[name](oldValue, newValue);
  }

  set customStrings(value: CustomStrings) {
    this._customStrings = value;
  }

  get customStrings() {
    return this._customStrings;
  }

  set onDataLoadingStarted(onDataLoadingStartFn: (() => Promise<void>) | undefined) {
    this.onDataLoadingStartedFn = onDataLoadingStartFn;
  }

  get onDataLoadingStarted(): (() => Promise<void>) | undefined {
    return this.onDataLoadingStartedFn;
  }

  set onDataLoadingFinishedWithSuccess(onDataLoadingFinishWithSuccessFn: (() => Promise<void>) | undefined) {
    this.onDataLoadingFinishedWithSuccessFn = onDataLoadingFinishWithSuccessFn;
  }

  get onDataLoadingFinishedWithSuccess(): (() => Promise<void>) | undefined {
    return this.onDataLoadingFinishedWithSuccessFn;
  }

  set onDataLoadingFinishedWithError(
    onDataLoadingFinishWithErrorFn: ((error?: undefined) => Promise<void>) | undefined
  ) {
    this.onDataLoadingFinishedWithErrorFn = onDataLoadingFinishWithErrorFn;
  }

  get onDataLoadingFinishedWithError(): ((error?: undefined) => Promise<void>) | undefined {
    return this.onDataLoadingFinishedWithErrorFn;
  }

  get viewId(): string {
    const value = this.getAttribute('data-view-id');
    return value ? value : '';
  }

  set viewId(value: string) {
    this.setAttribute('data-view-id', value);
  }

  get isCompactViewChecked(): boolean | undefined {
    if (this.hasAttribute('data-is-compact-view-checked')) {
      const value = this.getAttribute('data-is-compact-view-checked');
      return value ? JSON.parse(value) : undefined;
    }
    return undefined;
  }

  set isCompactViewChecked(value: boolean | undefined) {
    if (value === true || value === false) {
      this.setAttribute('data-is-compact-view-checked', JSON.stringify(value ? true : false));
    } else {
      this.removeAttribute('data-is-compact-view-checked');
    }
  }

  set filters(value: Filter<ColumnKeys>[]) {
    this.setAttribute('data-filters', JSON.stringify({ filters: value }));
  }

  get filters(): Filter<ColumnKeys>[] {
    return this.getArrayValueFromAttribute(this, 'data-filters', 'filters', []);
  }

  get extraParams(): any {
    return this.getArrayValueFromAttribute(this, 'data-extra-params', 'extraParams', {});
  }

  set pageSize(value: number) {
    this.setAttribute('data-page-size', JSON.stringify(value));
  }

  get pageSize(): number {
    const pageSize = this.getAttribute('data-page-size');
    return pageSize ? parseInt(pageSize, 10) : 50;
  }

  set pageNumber(value: number) {
    this.setAttribute('data-page-number', JSON.stringify(value));
  }

  get pageNumber(): number {
    const pageNumber = this.getAttribute('data-page-number');
    return pageNumber ? parseInt(pageNumber, 10) : 1;
  }

  set extraParams(value: any) {
    this.setAttribute('data-extra-params', JSON.stringify({ extraParams: value }));
  }

  set predefinedFiltersKeys(value: ColumnKeys[]) {
    this.setAttribute('data-predefined-filters-keys', JSON.stringify({ predefinedFiltersKeys: value }));
  }

  get predefinedFiltersKeys(): ColumnKeys[] {
    return this.getArrayValueFromAttribute(this, 'data-predefined-filters-keys', 'predefinedFiltersKeys');
  }

  get columns(): Column[] {
    return this.getArrayValueFromAttribute(this, 'data-columns', 'columns');
  }

  set columns(value: Column[]) {
    this.setAttribute('data-columns', JSON.stringify({ columns: value }));
  }

  get allColumnDetails(): ColumnDetails<Column, ColumnKeys>[] {
    return this.getArrayValueFromAttribute(this, 'data-all-column-details', 'allColumnDetails');
  }

  set allColumnDetails(value: ColumnDetails<Column, ColumnKeys>[]) {
    this.setAttribute('data-all-column-details', JSON.stringify({ allColumnDetails: value }));
    if (this.controller?.view.dom.$gridFilters.allColumnDetails) {
      this.controller.view.dom.$gridFilters.allColumnDetails = value;
    }
  }

  set filterDependencyMapping(value) {
    this._filterDependencyMapping = value;
    if (this.controller?.view.dom.$gridFilters.filterDependencyMapping) {
      this.controller.view.dom.$gridFilters.filterDependencyMapping = value;
    }
  }

  get filterDependencyMapping() {
    return this._filterDependencyMapping;
  }

  get defaultViewProps(): ViewConfiguration<Column, ColumnKeys> | undefined {
    return this.getValueFromAttribute(this, 'data-default-view-props');
  }

  set defaultViewProps(view: ViewConfiguration<Column, ColumnKeys> | undefined) {
    if (!view) {
      this.removeAttribute('data-default-view-props');
      return;
    }
    this.setAttribute('data-default-view-props', JSON.stringify(view));
  }

  set columnRenderers(renderers: GridColumnRenderers | undefined) {
    this.columnRenderersObj = renderers;
  }

  get columnRenderers() {
    return this.columnRenderersObj;
  }

  set headerRenderers(renderers: GridHeaderRenderers | undefined) {
    this.headerRenderersObj = renderers;
  }

  get headerRenderers() {
    return this.headerRenderersObj;
  }

  set rowDetailsRenderer(rendererFn: Function | undefined) {
    this.rowDetailsRendererFn = rendererFn;
  }

  get rowDetailsRenderer() {
    return this.rowDetailsRendererFn;
  }

  set dataProvider(value: GridBaseDataProvider<Column, ColumnKeys>) {
    this.dataProviderRef = value;
  }

  get dataProvider(): GridBaseDataProvider<Column, ColumnKeys> {
    return this.dataProviderRef;
  }

  set sideMenuParent(parent: HTMLElement) {
    this.sideMenuParentRef = parent;
  }

  get sideMenuParent() {
    return this.sideMenuParentRef;
  }

  set removeFilters(value: boolean) {
    this.setRemoveAttribute(value, 'removeFilters');
  }

  get removeFilters(): boolean {
    return this.hasAttribute('removeFilters');
  }

  set hideHeaderActions(value: boolean) {
    this.setRemoveAttribute(value, 'hideHeaderActions');
  }

  get hideHeaderActions(): boolean {
    return this.hasAttribute('hideHeaderActions');
  }

  set hideColumnHeaders(value: boolean) {
    this.setRemoveAttribute(value, 'hideColumnHeaders');
  }

  get hideColumnHeaders(): boolean {
    return this.hasAttribute('hideColumnHeaders');
  }

  set hasSelectableRows(value: boolean) {
    this.setRemoveAttribute(value, 'hasSelectableRows');
  }

  get hasSelectableRows(): boolean {
    return this.hasAttribute('hasSelectableRows');
  }

  set hideFooter(value: boolean) {
    this.setRemoveAttribute(value, 'hideFooter');
  }

  get hideFooter(): boolean {
    return this.hasAttribute('hideFooter');
  }

  set showToolbarRefresh(value: boolean) {
    this.setRemoveAttribute(value, 'showToolbarRefresh');
  }

  get showToolbarRefresh(): boolean {
    return this.hasAttribute('showToolbarRefresh');
  }

  set hideGridRefresh(value: boolean) {
    this.setRemoveAttribute(value, 'hideGridRefresh');
  }

  get hideGridRefresh(): boolean {
    return this.hasAttribute('hideGridRefresh');
  }

  set hideFiltersToggle(value: boolean) {
    this.setRemoveAttribute(value, 'hideFiltersToggle');
  }

  get hideFiltersToggle(): boolean {
    return this.hasAttribute('hideFiltersToggle');
  }

  set disableGridSettings(value: boolean) {
    this.setRemoveAttribute(value, 'disableGridSettings');
  }

  get disableGridSettings(): boolean {
    return this.hasAttribute('disableGridSettings');
  }

  set inlineRowActions(value: Array<SelectOption> | undefined) {
    this.inlineRowActionsRef = value;
  }

  get inlineRowActions(): Array<SelectOption> | undefined {
    return this.inlineRowActionsRef;
  }

  get columnConfigs(): Array<GridColumnConfiguration> {
    return this._columnConfigs;
  }

  set columnConfigs(value: Array<GridColumnConfiguration>) {
    if (value.length === 0) {
      this._columnConfigs = [];
      return;
    }

    value.forEach(columnConfig => {
      const componentColumnConfig = this._columnConfigs.find(
        existingColumnConfig => existingColumnConfig.key === columnConfig.key
      );

      if (!componentColumnConfig) {
        this._columnConfigs.push(columnConfig);
      } else {
        componentColumnConfig.width = columnConfig.width;
      }
    });
  }

  /*
   We set this if we want a specific column to flexibly grow, if it has space.
   Useful when we also save the column widths, with the columnConfigs property
   */
  set defaultFlexGrowColumn(value: string) {
    this._defaultFlexGrowColumn = value;
  }

  get defaultFlexGrowColumn(): string {
    return this._defaultFlexGrowColumn;
  }

  set shouldLoadAsync(value: boolean) {
    this._shouldLoadAsync = value;
  }

  get shouldLoadAsync() {
    return this._shouldLoadAsync ?? false;
  }

  set showFilters(value: boolean) {
    this._showFilters = value;
  }

  get showFilters() {
    return this._showFilters ?? false;
  }

  /**
   * This is a flag to optionally hide the 'Reset filters' button if the grid has more than two filters.
   * @param value
   */
  set hideResetFilters(value: boolean) {
    this.setRemoveAttribute(value, 'hideResetFilters');
  }

  get hideResetFilters(): boolean {
    return this.hasAttribute('hideResetFilters');
  }

  set enableRowClickEvent(value: boolean) {
    this.setRemoveAttribute(value, 'enableRowClickEvent');
  }

  get enableRowClickEvent(): boolean {
    return this.hasAttribute('enableRowClickEvent');
  }

  set enableCSVExport(value: boolean) {
    this.setRemoveAttribute(value, 'enableCSVExport');
  }

  get enableCSVExport(): boolean {
    return this.hasAttribute('enableCSVExport');
  }

  set dynamicHeight(value: boolean) {
    this.setRemoveAttribute(value, 'dynamicHeight');
  }

  get dynamicHeight(): boolean {
    return this.hasAttribute('dynamicHeight');
  }

  set smartViewsEnabled(value: boolean) {
    this._smartViewsEnabled = value;
  }

  set favoriteIconConfig(value: any) {
    this._favoriteIconConfig = value;
  }

  get favoriteIconConfig(): any {
    return this._favoriteIconConfig ?? {};
  }

  get smartViewsEnabled(): boolean {
    return this._smartViewsEnabled ?? false;
  }

  set activeViewProps(value: ViewConfiguration<Column, ColumnKeys>) {
    this._activeViewProps = value;
  }

  get activeViewProps(): ViewConfiguration<Column, ColumnKeys> {
    return this._activeViewProps ?? {};
  }

  set activeViewInitialProps(value: ViewConfiguration<Column, ColumnKeys>) {
    this._activeViewInitialProps = value;
  }

  get activeViewInitialProps(): ViewConfiguration<Column, ColumnKeys> {
    return this._activeViewInitialProps ?? {};
  }

  set smartViewDiscardButtonDisabledState(value: boolean) {
    this._smartViewDiscardButtonDisabledState = value;
  }

  get smartViewDiscardButtonDisabledState(): boolean {
    return this._smartViewDiscardButtonDisabledState ?? false;
  }

  set smartViewSaveButtonDisabledState(value: boolean) {
    this._smartViewSaveButtonDisabledState = value;
  }

  get smartViewSaveButtonDisabledState(): boolean {
    return this._smartViewSaveButtonDisabledState ?? true;
  }

  set showConfigurableArea(show: boolean) {
    this._showConfigurableArea = show;
    this.setRemoveAttribute(show, 'showConfigurableArea');
  }

  get showConfigurableArea(): boolean {
    return this._showConfigurableArea;
  }

  set titleConfig(config: GridTitleConfig) {
    this.setRemoveAttribute(!!config, GridAttribute.TITLE, JSON.stringify(config));
    this._titleConfig = config;
  }

  set selectionStoreConfig(config: Partial<SelectionStoreConfig>) {
    this._selectionStoreConfig = {
      ...this._selectionStoreConfig,
      ...config,
    };
  }

  get selectionStoreConfig(): SelectionStoreConfig {
    return this._selectionStoreConfig;
  }

  get titleConfig(): GridTitleConfig {
    return this._titleConfig;
  }

  set showGlobalActions(show: boolean) {
    this._showGlobalActions = show;
    this.setRemoveAttribute(show, 'showGlobalActions');
  }

  get showGlobalActions(): boolean {
    return this._showGlobalActions;
  }

  private setRemoveAttribute(shouldSet: boolean, attributeName: string, attributeValue = ''): void {
    if (shouldSet) {
      this.setAttribute(attributeName, attributeValue);
    } else {
      this.removeAttribute(attributeName);
    }
  }

  set hasPagination(value: boolean) {
    if (value) {
      this.setAttribute('hasPagination', '');
    } else {
      this.removeAttribute('hasPagination');
    }
  }

  get hasPagination(): boolean {
    return this.hasAttribute('hasPagination');
  }

  set pageSizeOptions(value: Array<SelectOption> | undefined) {
    this.pageSizeOptionsRef = value;
  }

  get pageSizeOptions(): Array<SelectOption> | undefined {
    return this.pageSizeOptionsRef;
  }

  set selectedItems(value: Array<GridItemType> | undefined) {
    this.selectedItemsRef = value;
  }

  get selectedItems(): Array<GridItemType> | undefined {
    return this.selectedItemsRef;
  }

  set sortColumnName(value: string | undefined) {
    this._sortColumnName = value;
  }

  get sortColumnName(): string | undefined {
    return this._sortColumnName;
  }

  set sortDirection(value: string | undefined) {
    this._sortDirection = value;
  }

  get sortDirection(): string | undefined {
    return this._sortDirection;
  }

  set noEscapeSpaceInTitle(value: boolean) {
    this.setRemoveAttribute(value, 'noEscapeSpaceInTitle');
  }

  get noEscapeSpaceInTitle(): boolean {
    return this.hasAttribute('noEscapeSpaceInTitle');
  }

  set cellClassNameGenerator(callback: Function) {
    this._cellClassNameGenerator = callback;
  }

  get cellClassNameGenerator(): Function {
    return this._cellClassNameGenerator;
  }

  set hasSelectionExtraOptions(value: boolean) {
    this.setRemoveAttribute(value, GridAttribute.SELECTION_EXTRA_OPTIONS);
  }

  get hasSelectionExtraOptions(): boolean {
    return this.hasAttribute(GridAttribute.SELECTION_EXTRA_OPTIONS);
  }

  public closeSettingsSideMenu() {
    this.controller?.view.sideMenu.closeSideMenu();
  }

  set filtersUiConfigs(value: GridFilterUiConfiguration[]) {
    this._filtersUiConfigs = value;
  }

  get filtersUiConfigs(): GridFilterUiConfiguration[] {
    return this._filtersUiConfigs;
  }

  get isAllSelected(): boolean {
    return this.controller?.getSelectAllEnabled() as boolean;
  }

  get numbItems(): number {
    return this.controller?.numItems !== undefined ? this.controller?.numItems : 0;
  }

  public handleSmartViewChanges(): void {
    this.controller?.handleSmartViewChanges();
  }

  public isViewChanged(): boolean {
    return !!this.controller?.isViewChanged();
  }
}
